import { formatCurrency } from '@/lib/currency';
import { formatNumber } from '@/lib/helpers';
import type { FlowSummary } from '@/lib/api/email-flows';

/**
 * Calculate open rate percentage from opened and sent counts
 */
export function calculateOpenRate(opened: number, sent: number): string {
    if (!sent || sent === 0) return '0.0%';
    const percentage = (opened / sent) * 100;
    return `${formatNumber(percentage, { decimals: percentage >= 1 ? 0 : 1 })}%`;
}

/**
 * Calculate click rate percentage from clicked and sent counts
 */
export function calculateClickRate(clicked: number, sent: number): string {
    if (!sent || sent === 0) return '0.0%';
    const percentage = (clicked / sent) * 100;
    return `${formatNumber(percentage, { decimals: percentage >= 1 ? 0 : 1 })}%`;
}

/**
 * Format analytics count with optional compact notation
 */
export function formatAnalyticsCount(count: number, compact = true): string {
    return formatNumber(count, { compact });
}

/**
 * Get complete analytics data for a flow
 * Returns formatted strings ready for display
 */
export function getFlowAnalyticsData(flow: FlowSummary, currency: string, isMobile: boolean) {
    const compact = isMobile;
    const showTotalNumber = !isMobile;

    // Format sales with currency
    const sales = formatCurrency(flow.analytics?.sales ?? 0, currency, 'en-US', compact ? 0 : 2, compact);

    // Calculate percentages
    const openedPercentage = flow.analytics?.sent ? ((flow.analytics?.opened ?? 0) / flow.analytics.sent) * 100 : 0;

    const clickedPercentage = flow.analytics?.sent ? ((flow.analytics?.clicked ?? 0) / flow.analytics.sent) * 100 : 0;

    // Format percentages
    const displayOpenedPercentage = formatNumber(openedPercentage, {
        decimals: openedPercentage >= 1 ? 0 : 1,
    });

    const displayClickedPercentage = formatNumber(clickedPercentage, {
        decimals: clickedPercentage >= 1 ? 0 : 1,
    });

    // Format counts (only show on desktop)
    const openedNumber = showTotalNumber ? `(${formatNumber(flow.analytics?.opened ?? 0, { compact: true })})` : '';

    const clickedNumber = showTotalNumber ? `(${formatNumber(flow.analytics?.clicked ?? 0, { compact: true })})` : '';

    return {
        sales,
        sent: flow.analytics?.sent ?? 0,
        openRate: `${flow.analytics?.sent ? displayOpenedPercentage : '0.0'}% ${openedNumber}`,
        clickRate: `${flow.analytics?.sent ? displayClickedPercentage : '0.0'}% ${clickedNumber}`,
    };
}
